/* eslint-disable @typescript-eslint/naming-convention */

/**
 * `metadata` on the component-types API arrives as a protobuf Struct:
 * every value is wrapped in a `{ kind, <kind>_value }` envelope.
 */
type StructValueType = {
  string_value?: string
  number_value?: number
  bool_value?: boolean
  list_value?: { values?: StructValueType[] }
  struct_value?: StructType
}

type StructType = {
  fields?: Record<string, StructValueType>
}

/** How many linked records the component takes; `none` hides the picker. */
export type ReferenceModeType = 'single' | 'multiple' | 'none'

const REFERENCE_MODES: ReferenceModeType[] = ['single', 'multiple', 'none']

const toReferenceMode = (value: unknown): ReferenceModeType | '' =>
  REFERENCE_MODES.find((mode) => mode === value) || ''

export type ComponentTypeMetadataType = {
  /** Block layouts the component accepts, e.g. `1x3`. Empty when unrestricted. */
  allowedLayouts: string[]
  /** Redirection targets a tile can point at, e.g. `category`. */
  supportedRedirections: string[]
  minTiles: number
  maxTiles: number
  requireCustomImagePerTile: boolean
  /** Column counts a layout may not use, e.g. `[1, 2]` rules out `2x1` and `3x2`. Not enforced for now. */
  disallowedColumns: number[]
  /** Reserved for the "Semua" tab; the API flags them here. */
  isAllTabOnly: boolean
  /** Most components of this type one tab may hold. `0` when unlimited. */
  maxComponentSelected: number
  /** `reference_mode`; empty when the API leaves it out, so the drawer keeps its own default. */
  referenceMode: ReferenceModeType | ''
  /** `validation_rules.min_reference_ids` — fewest linked items a `multiple` type needs. `0` when unbounded. */
  minReferenceIds: number
  /** `validation_rules.max_reference_ids` — most linked items a `multiple` type takes. `0` when unbounded. */
  maxReferenceIds: number
  /** `validation_rules.min_reference_ids_tiles` — fewest Catalog tiles a Big Catalog needs. `0` when unbounded. */
  minReferenceIdsTiles: number
  /** `validation_rules.max_reference_ids_tiles` — most Catalog tiles a Big Catalog takes. `0` when unbounded. */
  maxReferenceIdsTiles: number
}

export const EMPTY_COMPONENT_TYPE_METADATA: ComponentTypeMetadataType = {
  allowedLayouts: [],
  supportedRedirections: [],
  minTiles: 0,
  maxTiles: 0,
  requireCustomImagePerTile: false,
  disallowedColumns: [],
  isAllTabOnly: false,
  maxComponentSelected: 0,
  referenceMode: '',
  minReferenceIds: 0,
  maxReferenceIds: 0,
  minReferenceIdsTiles: 0,
  maxReferenceIdsTiles: 0,
}

const getField = (struct: StructType | undefined, key: string) => struct?.fields?.[key]

const toStringList = (value?: StructValueType) =>
  (value?.list_value?.values || [])
    .map((item) => item.string_value || '')
    .filter((item): item is string => Boolean(item))

const toNumber = (value?: StructValueType) => value?.number_value || 0

const toNumberList = (value?: StructValueType) =>
  (value?.list_value?.values || []).map((item) => item.number_value || 0).filter((item) => item > 0)

const toBoolean = (value?: StructValueType) => Boolean(value?.bool_value)

/** Plain JSON values, as `metadata` now arrives (a JSON-encoded string). */
type PlainMetadataType = Record<string, unknown>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** `metadata` now ships as a JSON string; malformed or empty text reads as no metadata. */
const parseMetadataString = (metadata: string): PlainMetadataType | undefined => {
  try {
    const parsed: unknown = JSON.parse(metadata)

    return isRecord(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

const toPlainStringList = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && Boolean(item))
    : []

const toPlainNumber = (value: unknown) => (typeof value === 'number' ? value : 0)

const toPlainNumberList = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number') : []

/* The string form may carry the flag as `true` or `"true"`. */
const toPlainBoolean = (value: unknown) => value === true || value === 'true'

const parsePlainMetadata = (metadata: PlainMetadataType): ComponentTypeMetadataType => {
  const validationRules = isRecord(metadata.validation_rules) ? metadata.validation_rules : {}

  return {
    allowedLayouts: toPlainStringList(metadata.allowed_layouts),
    supportedRedirections: toPlainStringList(metadata.supported_redirections),
    minTiles: toPlainNumber(metadata.min_tiles),
    maxTiles: toPlainNumber(metadata.max_tiles),
    requireCustomImagePerTile: toPlainBoolean(validationRules.require_custom_image_per_tile),
    disallowedColumns: toPlainNumberList(validationRules.disallowed_columns),
    isAllTabOnly: toPlainBoolean(metadata.all_tab_only),
    maxComponentSelected: toPlainNumber(
      metadata.max_component_selected ?? validationRules.max_component_selected,
    ),
    referenceMode: toReferenceMode(metadata.reference_mode ?? validationRules.reference_mode),
    minReferenceIds: toPlainNumber(validationRules.min_reference_ids),
    maxReferenceIds: toPlainNumber(validationRules.max_reference_ids),
    minReferenceIdsTiles: toPlainNumber(validationRules.min_reference_ids_tiles),
    maxReferenceIdsTiles: toPlainNumber(validationRules.max_reference_ids_tiles),
  }
}

/** Reads the fields the drawer needs; anything missing falls back to "no rule". */
export const parseComponentTypeMetadata = (
  metadata?: string | Record<string, unknown> | null,
): ComponentTypeMetadataType => {
  if (typeof metadata === 'string') {
    const plain = parseMetadataString(metadata)

    return plain ? parsePlainMetadata(plain) : EMPTY_COMPONENT_TYPE_METADATA
  }

  const struct = (metadata || undefined) as StructType | undefined
  const validationRules = getField(struct, 'validation_rules')?.struct_value

  return {
    allowedLayouts: toStringList(getField(struct, 'allowed_layouts')),
    supportedRedirections: toStringList(getField(struct, 'supported_redirections')),
    minTiles: toNumber(getField(struct, 'min_tiles')),
    maxTiles: toNumber(getField(struct, 'max_tiles')),
    requireCustomImagePerTile: toBoolean(
      getField(validationRules, 'require_custom_image_per_tile'),
    ),
    disallowedColumns: toNumberList(getField(validationRules, 'disallowed_columns')),
    isAllTabOnly: toBoolean(getField(struct, 'all_tab_only')),
    maxComponentSelected: toNumber(
      getField(struct, 'max_component_selected') ||
        getField(validationRules, 'max_component_selected'),
    ),
    referenceMode: toReferenceMode(
      (getField(struct, 'reference_mode') || getField(validationRules, 'reference_mode'))
        ?.string_value,
    ),
    minReferenceIds: toNumber(getField(validationRules, 'min_reference_ids')),
    maxReferenceIds: toNumber(getField(validationRules, 'max_reference_ids')),
    minReferenceIdsTiles: toNumber(getField(validationRules, 'min_reference_ids_tiles')),
    maxReferenceIdsTiles: toNumber(getField(validationRules, 'max_reference_ids_tiles')),
  }
}

/** `2x4` → 8 tiles. Falls back to `0` for a layout the API did not shape that way. */
export const getLayoutTileCount = (layout: string) => {
  const [rows, columns] = layout.split('x').map(Number)

  return rows > 0 && columns > 0 ? rows * columns : 0
}

/**
 * Temporarily off at the owner's request: the API lists layouts in `allowed_layouts`
 * that `disallowed_columns` would rule out. Flip back to `true` to filter on it again.
 */
const IS_DISALLOWED_COLUMNS_ENABLED = false

/**
 * Layouts the block picker offers: the allowed ones whose column count is not
 * ruled out (while `IS_DISALLOWED_COLUMNS_ENABLED`) and whose tile count sits
 * within `min_tiles`..`max_tiles` (0 = no bound).
 */
export const getSelectableLayouts = ({
  allowedLayouts,
  disallowedColumns,
  minTiles,
  maxTiles,
}: ComponentTypeMetadataType) =>
  allowedLayouts.filter((layout) => {
    const columns = Number(layout.split('x')[1])
    const tileCount = getLayoutTileCount(layout)

    return (
      !(IS_DISALLOWED_COLUMNS_ENABLED && disallowedColumns.includes(columns)) &&
      (!minTiles || tileCount >= minTiles) &&
      (!maxTiles || tileCount <= maxTiles)
    )
  })
